import React, { useState } from 'react';
import { VisibilityIcon, VisibilityOffIcon } from './feather-icons';
import { useTranslation } from 'react-i18next';
import { authService } from '../services/auth.service';
import './rag-chat-shell.css';

interface SetNewPasswordDialogProps {
  open: boolean;
  user: {
    id: string;
    username: string;
    name: string;
    role: string;
  } | null;
  passwordResetToken?: string;
  onSuccess: (token: string) => void;
  onError: (error: string) => void;
}

const SetNewPasswordDialog: React.FC<SetNewPasswordDialogProps> = ({
  open,
  user,
  passwordResetToken,
  onSuccess,
  onError
}) => {
  const { t } = useTranslation();
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validation
    if (!newPassword) {
      setError(t('passwordRequired', { defaultValue: 'Password is required' }));
      setLoading(false);
      return;
    }

    if (newPassword.length < 6) {
      setError(t('passwordTooShort', { defaultValue: 'Password must be at least 6 characters long' }));
      setLoading(false);
      return;
    }

    try {
      const data = await authService.setNewPassword({
        ...(passwordResetToken
          ? { passwordResetToken }
          : { username: user?.username }),
        newPassword,
        confirmPassword: newPassword
      });

      // Success - pass token to parent
      onSuccess(data.token || '');

      // Reset form
      setNewPassword('');
      setError('');
    } catch (err: any) {
      const errorMessage = err.response?.data?.error
        || err.message
        || t('setPasswordError', { defaultValue: 'Failed to set new password' });
      setError(errorMessage);
      onError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClickShowPassword = () => setShowPassword(!showPassword);

  if (!open) {
    return null;
  }

  return (
    <div className="industrial-dialog-backdrop">
      <div
        className="industrial-dialog industrial-dialog--compact"
        role="dialog"
        aria-modal="true"
        aria-labelledby="set-new-password-title"
      >
        <form onSubmit={handleSubmit}>
          <div className="industrial-dialog-header">
            <h2 id="set-new-password-title">
              {t('setNewPasswordTitle', { defaultValue: 'Set New Password' })}
            </h2>
          </div>
          <div className="industrial-dialog-body">
            <p className="industrial-dialog-copy">
              {t('setNewPasswordDescription', {
                defaultValue: 'Your password has been reset. Please enter a new password to continue.'
              })}
            </p>

            {user && (
              <div className="industrial-user-summary">
                <div>
                  <span>{t('username', { defaultValue: 'Username' })}</span>
                  <strong>{user.username}</strong>
                </div>
                <div>
                  <span>{t('name', { defaultValue: 'Name' })}</span>
                  <strong>{user.name}</strong>
                </div>
              </div>
            )}

            <label className="industrial-field industrial-field--full">
              <span>{t('newPassword', { defaultValue: 'New Password' })}</span>
              <div className="industrial-password-control">
                <input
                  autoFocus
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  aria-label={t('togglePasswordVisibility', { defaultValue: 'Toggle password visibility' })}
                  onClick={handleClickShowPassword}
                >
                  {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                </button>
              </div>
              <small>{t('passwordMinLength', { defaultValue: 'Minimum 6 characters. Use the eye icon to show/hide password.' })}</small>
            </label>

            {error && (
              <div className="industrial-alert industrial-alert--error">
                {error}
              </div>
            )}
          </div>
          <div className="industrial-dialog-footer">
            <button
              type="submit"
              className="industrial-button industrial-button--full"
              disabled={loading || !newPassword || newPassword.length < 6}
            >
              {loading ? t('settingPassword', { defaultValue: 'Setting Password...' }) : t('setPassword', { defaultValue: 'Set Password' })}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SetNewPasswordDialog;
